import React, { useState, useEffect, useMemo, useRef } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { NumberField } from "../../../shared/components/NumberField.jsx";
import { QuantityCalculatorSheet } from "../../../shared/components/QuantityCalculatorSheet.jsx";
import { useItems } from "../../items/hooks/useItems.js";
import { useCompanyParties } from "../../customers/hooks/useCompanyParties.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { ItemForm } from "../../items/components/ItemForm.jsx";
import { itemInCompany } from "../../items/models/item.js";
import { formatInvoiceNumber } from "../../companies/models/company.js";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import {
  calculateLineItem,
  calculateInvoiceTotals,
  generateInvoiceNumber,
  SELLER_CONFIG,
  INVOICE_TYPES
} from "../models/invoice.js";
import {
  createDefaultShippingAddress,
  getCustomerPriceForItem
} from "../../customers/models/customer.js";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { useActiveCompany, useCompanyLock } from "../../../shared/context/ActiveCompanyContext.jsx";
import { updateInvoice } from "../services/invoiceService.js";
import { diffInvoice } from "../utils/invoiceEdit.js";

/**
 * Create a new invoice, or edit a saved one (editingInvoice): the same 5 steps, filled in.
 * When editing, the company and invoice number / type are locked, and the customer is locked
 * once receipts are recorded against the invoice.
 */
export function InvoiceCreateFlow({ isOpen, onClose, onInvoiceCreated, initialCompanyId, editingInvoice = null, onInvoiceUpdated }) {
  const isEditing = !!editingInvoice;
  const { confirmDanger } = useDangerConfirm();
  const { items, addItem: createItemInline, refetch: refetchItems } = useItems();
  // Only the working company's customers (header), with their due for that company
  const { customers, fetchAddresses, addAddress, saveCustomerPrices, dueOf } = useCompanyParties();
  const { companies, refetch: refetchCompanies } = useCompanies();
  // New invoices are always for the company picked in the header (locked while this is open)
  const { activeCompany } = useActiveCompany();
  useCompanyLock(isOpen);

  // Wizard Step: 1 = Company & Bill, 2 = Customer / Party, 3 = Shipping, 4 = Items & Rates, 5 = Review & Save
  const [step, setStep] = useState(1);

  // Selected Issuing Company
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Invoice Mode: 'GST' (Tax Invoice) or 'Normal' (Non-GST / Cash Bill)
  const [invoiceType, setInvoiceType] = useState(INVOICE_TYPES.GST);

  // Form State
  const [invoiceNo, setInvoiceNo] = useState("1");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [lorryNo, setLorryNo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [discountInput, setDiscountInput] = useState(0);
  const [paymentOption, setPaymentOption] = useState("Unpaid"); // 'Unpaid' | 'Paid'

  // Map of { [itemId]: rate } tracking any custom rates set for this customer
  const [customPriceOverrides, setCustomPriceOverrides] = useState({});

  // Provision to add a new shipping address directly in invoice flow
  const [showAddNewAddress, setShowAddNewAddress] = useState(false);
  const [newAddressForm, setNewAddressForm] = useState(createDefaultShippingAddress());
  const [savingNewAddress, setSavingNewAddress] = useState(false);

  // Item Picker State for Step 4
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [showAddItemSheet, setShowAddItemSheet] = useState(false);
  const [qtyCalcIndex, setQtyCalcIndex] = useState(null); // line whose qty is being added up
  const [savingNewItem, setSavingNewItem] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const prevIsOpenRef = useRef(false);

  // Reset ONLY on initial modal open transition (false -> true)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && editingInvoice) {
      // Edit: fill every step from the saved invoice and start on Items (the usual fix)
      const inv = editingInvoice;
      const company = companies.find((c) => c.id === inv.companyId) || { id: inv.companyId || "", ...(inv.companyDetails || {}), name: inv.companyName || inv.companyDetails?.name || "" };
      const customer = customers.find((c) => c.id === inv.customerId) || {
        id: inv.customerId, name: inv.customerName, gstin: inv.customerGstin || "", mobile: inv.customerPhone || "",
        city: inv.customerCity || "", state: inv.customerState || "", stateCode: inv.customerStateCode || "", billingAddress: inv.billingAddress || {}
      };
      setSelectedCompany(company);
      setInvoiceType(inv.invoiceType === INVOICE_TYPES.NORMAL || inv.isNonGst ? INVOICE_TYPES.NORMAL : INVOICE_TYPES.GST);
      setInvoiceNo(inv.invoiceNo || "");
      setInvoiceDate(inv.invoiceDate || new Date().toISOString().split("T")[0]);
      setLorryNo(inv.lorryNo || "");
      setSelectedCustomer(customer);
      setSelectedShippingAddress(inv.shippingAddress || null);
      setShowAddNewAddress(false);
      setLineItems((inv.lineItems || []).map((li) => ({ ...li })));
      setDiscountInput(Number(inv.discountAmount || 0));
      const paid = Number(inv.paid !== undefined ? inv.paid : (inv.paidAmount || 0));
      const receipts = (inv.paymentHistory || []).filter((h) => !h.voided && Number(h.amount || 0) > 0);
      setPaymentOption(!receipts.length && paid >= Number(inv.grandTotal || 0) - 0.005 && paid > 0 ? "Paid" : "Unpaid");
      setCustomPriceOverrides({});
      setError("");
      setStep(4);
      if (inv.customerId) fetchAddresses(inv.customerId).then(setShippingAddresses).catch(() => setShippingAddresses([]));
    } else if (isOpen && !prevIsOpenRef.current) {
      setStep(1);
      const billingCompany = activeCompany || (initialCompanyId && companies.find((c) => c.id === initialCompanyId)) || null;
      setSelectedCompany(billingCompany);
      setInvoiceType(INVOICE_TYPES.GST);
      setInvoiceNo(formatInvoiceNumber(billingCompany, false));
      setLorryNo("");
      setSelectedCustomer(null);
      setShippingAddresses([]);
      setSelectedShippingAddress(null);
      setShowAddNewAddress(false);
      setLineItems([]);
      setDiscountInput(0);
      setPaymentOption("Unpaid");
      setCustomPriceOverrides({});
      setError("");
      if (refetchCompanies) refetchCompanies();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Initial company fallback only if companies were empty when modal opened
  useEffect(() => {
    if (isOpen && !isEditing && !selectedCompany && activeCompany) {
      setSelectedCompany(activeCompany);
      const isNonGst = invoiceType === INVOICE_TYPES.NORMAL;
      setInvoiceNo(formatInvoiceNumber(activeCompany, isNonGst));
    }
  }, [isOpen, isEditing, selectedCompany, activeCompany, invoiceType]);

  // Keep selectedCompany object updated with background company list changes without resetting form
  useEffect(() => {
    if (selectedCompany && companies.length > 0) {
      const fresh = companies.find((c) => c.id === selectedCompany.id);
      if (fresh && (fresh.nextInvoiceNumber !== selectedCompany.nextInvoiceNumber || fresh.nonGstNextInvoiceNumber !== selectedCompany.nonGstNextInvoiceNumber || fresh.name !== selectedCompany.name)) {
        setSelectedCompany(fresh);
      }
    }
  }, [companies, selectedCompany]);

  // Handle inline item creation: save to inventory, then add it straight to the invoice
  const handleSaveItemInline = async (itemData) => {
    setSavingNewItem(true);
    try {
      const newId = await createItemInline(itemData);
      handleAddItem({ id: newId, ...itemData });
      setItemSearchTerm("");
      setShowAddItemSheet(false);
    } finally {
      setSavingNewItem(false);
    }
  };

  // Handle invoice type toggle (GST vs Non-GST)
  const handleToggleInvoiceType = (type) => {
    setInvoiceType(type);
    const isNonGst = type === INVOICE_TYPES.NORMAL;
    if (selectedCompany) {
      setInvoiceNo(formatInvoiceNumber(selectedCompany, isNonGst));
    } else {
      setInvoiceNo(generateInvoiceNumber(type));
    }
    // Recalculate existing line items with or without GST
    setLineItems((prev) =>
      prev.map((li) => {
        const catalogItem = items.find((i) => i.id === li.itemId) || { ...li, id: li.itemId };
        return calculateLineItem(catalogItem, li.qty, li.rate, isNonGst);
      })
    );
  };

  // When a customer is picked, load shipping addresses and auto-select 1st address
  const handleSelectCustomer = async (cust) => {
    setSelectedCustomer(cust);
    setError("");
    try {
      const addrs = await fetchAddresses(cust.id);
      setShippingAddresses(addrs);
      if (addrs && addrs.length > 0) {
        const firstOrPreferred = addrs.find((a) => a.isDefault) || addrs[0];
        setSelectedShippingAddress(firstOrPreferred);
      } else {
        setSelectedShippingAddress(null);
      }
    } catch (err) {
      console.warn("Could not load customer shipping addresses:", err);
      setSelectedShippingAddress(null);
    }
  };

  // Calculate totals live (incorporating invoiceType GST vs Non-GST and issuing company state)
  const totals = useMemo(() => {
    return calculateInvoiceTotals(lineItems, selectedCustomer, discountInput, invoiceType, selectedCompany);
  }, [lineItems, selectedCustomer, discountInput, invoiceType, selectedCompany]);

  // Edit: what saving will change (same calculation the save uses)
  const editChange = useMemo(() => {
    if (!isEditing) return null;
    return diffInvoice(editingInvoice, {
      grandTotal: totals.grandTotal,
      lineItems,
      customerId: selectedCustomer?.id,
      customerName: selectedCustomer?.name,
      invoiceDate,
      paidInFull: paymentOption === "Paid"
    });
  }, [isEditing, editingInvoice, totals.grandTotal, lineItems, selectedCustomer, invoiceDate, paymentOption]);

  // Line Item Management: auto-fill rate using customer-specific price memory.
  // Every tap adds a separate line, so the same item can be billed with different qty/rate.
  const handleAddItem = (item) => {
    const isNonGst = invoiceType === INVOICE_TYPES.NORMAL;

    // Look up customer-specific custom price override, if any
    const customerRate = getCustomerPriceForItem(selectedCustomer, item);

    setLineItems((prev) => [...prev, calculateLineItem(item, 1, customerRate, isNonGst)]);
  };

  const handleUpdateLineQty = (index, newQty, qtyBreakdown = undefined) => {
    const isNonGst = invoiceType === INVOICE_TYPES.NORMAL;
    const parsed = parseFloat(newQty);
    const qty = isNaN(parsed) ? 1 : Math.max(0.001, parsed);
    const target = lineItems[index];
    if (!target) return;
    const originalItem = items.find((i) => i.id === target.itemId) || { ...target, id: target.itemId }; // keep the item link if the catalogue is still loading
    const updated = [...lineItems];
    const resolvedBreakdown = qtyBreakdown !== undefined
      ? qtyBreakdown
      : (Array.isArray(target.qtyBreakdown) && Math.abs(target.qtyBreakdown.reduce((a, b) => a + b, 0) - qty) < 0.001 ? target.qtyBreakdown : null);
    updated[index] = calculateLineItem(originalItem, qty, target.rate, isNonGst, resolvedBreakdown);
    setLineItems(updated);
  };

  const handleQtyChange = handleUpdateLineQty;

  const handleQtyCalcApply = (index, total, breakdown) => {
    handleUpdateLineQty(index, total, breakdown);
  };

  // Update item rate specifically for this customer while creating invoice
  const handleUpdateLineRate = (index, newRate) => {
    const isNonGst = invoiceType === INVOICE_TYPES.NORMAL;
    const parsed = parseFloat(newRate);
    const rate = isNaN(parsed) ? 0 : Math.max(0, parsed);
    const target = lineItems[index];
    if (!target) return;
    const originalItem = items.find((i) => i.id === target.itemId) || { ...target, id: target.itemId }; // keep the item link if the catalogue is still loading
    const updated = [...lineItems];
    updated[index] = calculateLineItem(originalItem, target.qty, rate, isNonGst);
    setLineItems(updated);

    // Track custom price override for this customer
    if (target.itemId) {
      setCustomPriceOverrides((prev) => ({
        ...prev,
        [target.itemId]: rate
      }));
    }
  };

  const handleRateChange = handleUpdateLineRate;

  const handleRemoveLine = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Inline shipping address creation handler
  const handleSaveNewAddress = async (e) => {
    e.preventDefault();
    if (!newAddressForm.addressLine1.trim() || !newAddressForm.city.trim()) {
      setError("Please provide Address Line 1 and City for the new shipping location.");
      return;
    }

    setSavingNewAddress(true);
    setError("");
    try {
      const newId = await addAddress(selectedCustomer.id, newAddressForm);
      const createdObj = { id: newId, ...newAddressForm };
      const updatedList = [...shippingAddresses, createdObj];
      setShippingAddresses(updatedList);
      setSelectedShippingAddress(createdObj);
      setShowAddNewAddress(false);
      setNewAddressForm(createDefaultShippingAddress());
    } catch (err) {
      setError(err.message || "Failed to add shipping address.");
    } finally {
      setSavingNewAddress(false);
    }
  };

  // Filter customers based on search term (only include parties that are customers or both)
  const filteredCustomers = useMemo(() => {
    const salesParties = customers.filter(
      (c) => !c.partyType || c.partyType === "customer" || c.partyType === "both"
    );
    if (!customerSearchTerm.trim()) return salesParties;
    const term = customerSearchTerm.toLowerCase();
    return salesParties.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.city || "").toLowerCase().includes(term) ||
      (c.gstin || "").toLowerCase().includes(term) ||
      (c.mobile || "").includes(term)
    );
  }, [customers, customerSearchTerm]);

  // Step Validation & Navigation
  const handleNext = () => {
    setError("");
    if (step === 1) {
      if (!invoiceNo.trim()) {
        setError("Please enter a valid invoice or bill number.");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!selectedCustomer) {
        setError("Please select a customer or party to proceed.");
        return;
      }
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      if (lineItems.length === 0) {
        setError("Please add at least one line item to the invoice.");
        return;
      }
      setStep(5);
    }
  };

  const handleBack = () => {
    setError("");
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const isPaid = paymentOption === "Paid";
      const invoicePayload = {
        invoiceNo,
        invoiceDate,
        lorryNo: lorryNo.trim(),
        invoiceType, // 'GST' or 'Normal'
        companyId: selectedCompany?.id || "",
        companyName: selectedCompany?.name || (SELLER_CONFIG.name),
        companyGstin: selectedCompany?.gstin || (SELLER_CONFIG.gstin),
        defaultTemplate: selectedCompany?.defaultTemplate || "modern-tax",
        companyDetails: selectedCompany ? {
          name: selectedCompany.name,
          tradeName: selectedCompany.tradeName || "",
          gstin: selectedCompany.gstin || "",
          phone: selectedCompany.phone || "",
          email: selectedCompany.email || "",
          addressLine1: selectedCompany.addressLine1 || "",
          city: selectedCompany.city || "",
          state: selectedCompany.state || "Tamil Nadu",
          stateCode: selectedCompany.stateCode || "33",
          pincode: selectedCompany.pincode || "",
          bankName: selectedCompany.bankName || "",
          accountNumber: selectedCompany.accountNumber || "",
          ifscCode: selectedCompany.ifscCode || "",
          branchName: selectedCompany.branchName || "",
          defaultTemplate: selectedCompany.defaultTemplate || "modern-tax",
          invoiceCopies: selectedCompany.invoiceCopies || 2,
          dateFormat: selectedCompany.dateFormat || "DD-MM-YYYY"
        } : SELLER_CONFIG,
        sellerConfig: selectedCompany ? {
          name: selectedCompany.name,
          gstin: selectedCompany.gstin || "",
          state: selectedCompany.state || "Tamil Nadu",
          stateCode: selectedCompany.stateCode || "33",
          address: `${selectedCompany.addressLine1 || ""}, ${selectedCompany.city || ""} ${selectedCompany.pincode || ""}`.trim(),
          phone: selectedCompany.phone || "",
          email: selectedCompany.email || "",
          bankName: selectedCompany.bankName || "",
          accountNumber: selectedCompany.accountNumber || "",
          ifscCode: selectedCompany.ifscCode || "",
          branchName: selectedCompany.branchName || "",
          defaultTemplate: selectedCompany.defaultTemplate || "modern-tax",
          invoiceCopies: selectedCompany.invoiceCopies || 2,
          dateFormat: selectedCompany.dateFormat || "DD-MM-YYYY"
        } : SELLER_CONFIG,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        customerGstin: invoiceType === INVOICE_TYPES.GST ? (selectedCustomer.gstin || "") : "",
        customerPhone: selectedCustomer.mobile || "",
        customerCity: selectedCustomer.city || "",
        customerState: selectedCustomer.state || "Tamil Nadu",
        customerStateCode: selectedCustomer.stateCode || "33",
        billingAddress: selectedCustomer.billingAddress || {},
        shippingAddress: selectedShippingAddress || {
          label: "Billing Address",
          addressLine1: selectedCustomer.billingAddress?.addressLine1 || selectedCustomer.city || "",
          city: selectedCustomer.city || "",
          state: selectedCustomer.state || "Tamil Nadu",
          pincode: selectedCustomer.billingAddress?.pincode || ""
        },
        lineItems,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        taxableAmount: totals.taxableAmount,
        gstTotal: totals.gstTotal,
        cgst: totals.cgst,
        sgst: totals.sgst,
        igst: totals.igst,
        isInterState: totals.isInterState,
        isNonGst: totals.isNonGst,
        roundOff: totals.roundOff,
        grandTotal: totals.grandTotal,
        paid: isPaid ? totals.grandTotal : 0,
        balance: isPaid ? 0 : totals.grandTotal,
        paymentStatus: isPaid ? "Paid" : "Unpaid",
        updatedItemPrices: customPriceOverrides // Automatically updates customer-specific item prices in Firestore
      };

      if (isEditing) {
        if (editChange?.receipts.length) {
          const ok = await confirmDanger({
            title: `Save changes to ${invoiceNo}?`,
            message: `${editChange.summary.join("\n")}\n\nReceipts already recorded (₹${editChange.recorded.toLocaleString("en-IN")}) stay as they are.`,
            confirmLabel: "Save changes"
          });
          if (!ok) return;
        }
        await updateInvoice(editingInvoice.id, invoicePayload, {
          expectedUpdatedAt: editingInvoice.updatedAt,
          paidInFull: paymentOption === "Paid"
        });
        if (onInvoiceUpdated) await onInvoiceUpdated();
        refetchItems?.();
      } else {
        await onInvoiceCreated(invoicePayload);
        if (refetchCompanies) await refetchCompanies();
      }

      // Save custom item prices to local customer state if updated
      if (Object.keys(customPriceOverrides).length > 0) {
        await saveCustomerPrices(selectedCustomer.id, customPriceOverrides);
      }

      onClose();
    } catch (err) {
      console.error(isEditing ? "Invoice update failed:" : "Invoice creation failed:", err);
      setError(err.message || (isEditing ? "Failed to save changes." : "Failed to create invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  // How many cart lines each item has (the same item can appear on several lines)
  const cartCountByItem = useMemo(() => {
    const counts = {};
    lineItems.forEach((li) => {
      if (li.itemId) counts[li.itemId] = (counts[li.itemId] || 0) + 1;
    });
    return counts;
  }, [lineItems]);

  // Filter items for Step 3 catalog picker
  const filteredCatalogItems = useMemo(() => {
    const term = itemSearchTerm.toLowerCase().trim();
    const companyId = selectedCompany?.id || activeCompany?.id;
    const scoped = items.filter((i) => itemInCompany(i, companyId));
    if (!term) return scoped;
    return scoped.filter((i) => {
      const name = (i.itemName || i.name || "").toLowerCase();
      const code = (i.gstCode || i.sku || "").toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [items, itemSearchTerm, selectedCompany, activeCompany]);

  // Prefill the inline new-item form with the current search text (memoized so typing in the form isn't reset)
  const newItemPrefill = useMemo(
    () => (showAddItemSheet && itemSearchTerm.trim() ? { itemName: itemSearchTerm.trim() } : null),
    [showAddItemSheet, itemSearchTerm]
  );

  return (
    <>
      <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing
        ? `✏️ Edit ${invoiceType === INVOICE_TYPES.GST ? "invoice" : "bill"} ${invoiceNo}`
        : `${invoiceType === INVOICE_TYPES.GST ? "GST Invoice" : "Cash / Non-GST Bill"} • ${invoiceNo}`}
    >
      <div>
        {/* Wizard Step Progress Bar */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
          background: "var(--bg-card)",
          padding: "0.5rem 0.75rem",
          borderRadius: "var(--radius-md)",
          overflowX: "auto",
          gap: "0.5rem"
        }}>
          {[
            { num: 1, label: "Bill Setup" },
            { num: 2, label: "Customer" },
            { num: 3, label: "Ship To" },
            { num: 4, label: "Items" },
            { num: 5, label: "Review" }
          ].map((s) => (
            <div
              key={s.num}
              onClick={() => {
                if (s.num === 1 || (s.num === 2 && invoiceNo) || (s.num > 2 && selectedCustomer)) {
                  setStep(s.num);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.72rem",
                fontWeight: step === s.num ? "700" : "500",
                color: step === s.num ? "var(--primary)" : (step > s.num ? "var(--success)" : "var(--text-dim)"),
                cursor: (s.num === 1 || selectedCustomer) ? "pointer" : "default",
                whiteSpace: "nowrap"
              }}
            >
              <span>{step > s.num ? "✓" : s.num}.</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Global Quick Shipping Banner (visible on Steps 4 and 5) */}
        {selectedCustomer && step >= 4 && (
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.5rem 0.75rem",
            background: "rgba(59, 130, 246, 0.08)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "var(--radius-md)",
            marginBottom: "0.85rem"
          }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: "0.5rem" }}>
              <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Ship to: </span>
              <span style={{ fontWeight: "700", fontSize: "0.82rem", color: "var(--text-main)" }}>
                {selectedShippingAddress ? (selectedShippingAddress.label || selectedShippingAddress.city) : "Billing Address"}
              </span>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginLeft: "4px" }}>
                ({selectedShippingAddress ? selectedShippingAddress.city : selectedCustomer.city})
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ minHeight: "26px", padding: "0 8px", fontSize: "0.72rem", whiteSpace: "nowrap" }}
              onClick={() => setStep(3)}
            >
              Change ✏️
            </button>
          </div>
        )}

        {error && (
          <div className="form-error" style={{ marginBottom: "1rem" }}>
            {error}
          </div>
        )}

        {/* STEP 1: COMPANY & BILL NUMBER / DATE CONFIGURATION */}
        {step === 1 && isEditing && (
          <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", fontSize: "0.85rem", marginBottom: "0.35rem" }}>
              <span style={{ color: "var(--text-muted)" }}>Company</span>
              <span style={{ fontWeight: 700, textAlign: "right" }}>{selectedCompany?.name || editingInvoice.companyName} 🔒</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", fontSize: "0.85rem", marginBottom: "0.6rem" }}>
              <span style={{ color: "var(--text-muted)" }}>Number</span>
              <span style={{ fontWeight: 700 }}>{invoiceNo} ({invoiceType === INVOICE_TYPES.GST ? "GST" : "Non-GST"}) 🔒</span>
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginBottom: "0.6rem" }}>
              Company and number can't change on a saved invoice, so the numbering stays in order.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              <Input id="invoice-date-input" label="Date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} required />
              <Input id="invoice-lorry-input" label="Lorry / Vehicle No" value={lorryNo} onChange={(e) => setLorryNo(e.target.value)} placeholder="e.g. TN-67-AB-1234" />
            </div>
          </div>
        )}

        {step === 1 && !isEditing && (
          <div>
            {/* 1. Bill Type Selector: GST vs Non-GST (Top Level Choice) */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: "0.4rem" }}>
                1. Select Billing Classification
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                <button
                  type="button"
                  className={`btn ${invoiceType === INVOICE_TYPES.GST ? "btn-primary" : "btn-secondary"}`}
                  style={{ minHeight: "40px", fontSize: "0.82rem" }}
                  onClick={() => handleToggleInvoiceType(INVOICE_TYPES.GST)}
                >
                  🧾 GST Tax Invoice
                </button>
                <button
                  type="button"
                  className={`btn ${invoiceType === INVOICE_TYPES.NORMAL ? "btn-primary" : "btn-secondary"}`}
                  style={{ minHeight: "40px", fontSize: "0.82rem" }}
                  onClick={() => handleToggleInvoiceType(INVOICE_TYPES.NORMAL)}
                >
                  📄 Non-GST / Cash Bill
                </button>
              </div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "4px" }}>
                {invoiceType === INVOICE_TYPES.GST
                  ? "Applies statutory CGST+SGST or IGST with GSTIN compliance."
                  : "0% GST tax applied. Uses separate Non-GST invoice number sequence."}
              </div>
            </div>

            {/* 2. Issuing Company Selection */}
            <div style={{
              marginBottom: "1rem",
              background: "var(--bg-card)",
              padding: "0.75rem",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)"
            }}>
              <div data-testid="billing-company" style={{ marginBottom: "0.25rem" }}>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--text-main)" }}>
                  Billing as 🏢 {selectedCompany?.name || "—"}
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
                  To bill another company, switch it in the header first.
                </div>
              </div>

              {/* Configured Invoice Number Display & Edit */}
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.5rem", marginTop: "0.75rem" }}>
                <Input
                  id="invoice-number-input"
                  label={invoiceType === INVOICE_TYPES.NORMAL ? "Non-GST Bill #" : "GST Invoice #"}
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  placeholder="e.g. 1 or INV-1"
                  required
                />
                <Input
                  id="invoice-date-input"
                  label="Date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  required
                />
              </div>

              {/* Lorry / Vehicle / Transport Number */}
              <div style={{ marginTop: "0.5rem" }}>
                <Input
                  id="invoice-lorry-input"
                  label="Lorry / Vehicle / Transport No (Optional)"
                  value={lorryNo}
                  onChange={(e) => setLorryNo(e.target.value)}
                  placeholder="e.g. TN-67-AB-1234 / KPN / VRL"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: CUSTOMER / PARTY SELECTION */}
        {step === 2 && editChange?.customerLocked && (
          <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{selectedCustomer?.name} 🔒</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 4 }}>
              Receipts ({editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ") || "recorded"}) belong to this customer. Void them first to bill another customer.
            </div>
          </div>
        )}

        {step === 2 && !editChange?.customerLocked && (
          <div>
            <div style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--text-main)", marginBottom: "0.5rem" }}>
              Select Customer / Party
            </div>

            {/* Customer Search Box */}
            <div style={{ marginBottom: "0.75rem" }}>
              <input
                type="text"
                className="form-control"
                placeholder="🔍 Search party by name, city, GSTIN, mobile..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                style={{ fontSize: "0.85rem", minHeight: "40px" }}
              />
            </div>

            {customers.length === 0 ? (
              <div className="state-box" style={{ padding: "1.5rem" }}>
                <div>No customers found in database.</div>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="state-box" style={{ padding: "1.5rem" }}>
                <div>No customers match "{customerSearchTerm}".</div>
              </div>
            ) : (
              <div style={{ maxHeight: "280px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {filteredCustomers.map((c) => {
                  const isSelected = selectedCustomer?.id === c.id;
                  const customPriceCount = c.customPrices ? Object.keys(c.customPrices).length : 0;

                  return (
                    <div
                      key={c.id}
                      onClick={() => handleSelectCustomer(c)}
                      style={{
                        padding: "0.85rem",
                        background: isSelected ? "rgba(59, 130, 246, 0.15)" : "var(--bg-card)",
                        border: isSelected ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)",
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer",
                        transition: "all 0.15s"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: "600" }}>{c.name}</span>
                        <div style={{ display: "flex", gap: "4px" }}>
                          {c.gstin && <Badge variant="info" style={{ fontSize: "0.65rem" }}>GST</Badge>}
                          {customPriceCount > 0 && (
                            <Badge variant="success" style={{ fontSize: "0.65rem" }}>
                              {customPriceCount} Special Rates
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", marginTop: "2px", display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                        <span>{c.city || "Unknown City"} • {c.state || "Tamil Nadu"} ({c.stateCode || "33"})</span>
                        {dueOf(c) > 0.005 && <span style={{ color: "var(--warning)", fontWeight: 600, whiteSpace: "nowrap" }}>Due ₹{dueOf(c).toLocaleString("en-IN")}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected Shipping Destination Preview Box */}
            {selectedCustomer && (
              <div style={{
                marginTop: "0.85rem",
                padding: "0.85rem",
                background: "rgba(59, 130, 246, 0.08)",
                border: "1px solid rgba(59, 130, 246, 0.25)",
                borderRadius: "var(--radius-md)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ fontSize: "0.72rem", fontWeight: "700", color: "#93c5fd", textTransform: "uppercase" }}>
                    📍 Selected Party: {selectedCustomer.name}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: "0.72rem", padding: "0.2rem 0.6rem", minHeight: "28px" }}
                    onClick={() => setStep(3)}
                  >
                    Configure Shipping ✏️
                  </button>
                </div>
                <div style={{ fontWeight: "700", fontSize: "0.92rem", color: "var(--text-main)" }}>
                  {selectedShippingAddress ? (selectedShippingAddress.label || "First Shipping Address") : "🏢 Billing Address"}
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "2px" }}>
                  {selectedShippingAddress
                    ? `${selectedShippingAddress.addressLine1}, ${selectedShippingAddress.city} - ${selectedShippingAddress.pincode || ""}`
                    : (selectedCustomer.billingAddress?.addressLine1 || selectedCustomer.city)}
                </div>
                {shippingAddresses.length > 1 && (
                  <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "4px" }}>
                    ({shippingAddresses.length} saved destinations available for this customer)
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 3: SHIPPING DESTINATION SELECTION & CHANGE PROVISION */}
        {step === 3 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--text-main)" }}>
                Shipping Destination
              </div>
              {!showAddNewAddress && (
                <Button
                  size="sm"
                  variant="secondary"
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem", minHeight: "28px" }}
                  onClick={() => setShowAddNewAddress(true)}
                >
                  + Add New Address
                </Button>
              )}
            </div>

            <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", marginBottom: "0.85rem" }}>
              The 1st saved address was selected by default. Tap any option below to change the shipping destination for <strong>{selectedCustomer?.name}</strong>.
            </div>

            {/* If user clicked "+ Add New Address" */}
            {showAddNewAddress ? (
              <form onSubmit={handleSaveNewAddress} style={{ background: "var(--bg-card)", padding: "0.85rem", borderRadius: "var(--radius-md)", marginBottom: "1rem" }}>
                <div style={{ fontWeight: "600", fontSize: "0.88rem", marginBottom: "0.5rem" }}>
                  New Shipping Destination
                </div>
                <input
                  type="text"
                  placeholder="Location Tag / Label (e.g. Mill Unit 3, Site B)"
                  value={newAddressForm.label}
                  onChange={(e) => setNewAddressForm({ ...newAddressForm, label: e.target.value })}
                  className="form-control"
                  style={{ marginBottom: "0.5rem" }}
                  required
                />
                <input
                  type="text"
                  placeholder="Address Line 1 (Street / Building)"
                  value={newAddressForm.addressLine1}
                  onChange={(e) => setNewAddressForm({ ...newAddressForm, addressLine1: e.target.value })}
                  className="form-control"
                  style={{ marginBottom: "0.5rem" }}
                  required
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
                  <input
                    type="text"
                    placeholder="City"
                    value={newAddressForm.city}
                    onChange={(e) => setNewAddressForm({ ...newAddressForm, city: e.target.value })}
                    className="form-control"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Pincode"
                    value={newAddressForm.pincode}
                    onChange={(e) => setNewAddressForm({ ...newAddressForm, pincode: e.target.value })}
                    className="form-control"
                  />
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAddNewAddress(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    loading={savingNewAddress}
                  >
                    Save & Select This Address
                  </Button>
                </div>
              </form>
            ) : (
              <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {/* List of Subcollection Shipping Addresses */}
                {shippingAddresses.map((addr, idx) => {
                  const isSelected = selectedShippingAddress?.id === addr.id;
                  return (
                    <div
                      key={addr.id}
                      onClick={() => setSelectedShippingAddress(addr)}
                      style={{
                        padding: "0.85rem",
                        background: isSelected ? "rgba(59, 130, 246, 0.15)" : "var(--bg-card)",
                        border: isSelected ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)",
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        transition: "all 0.15s"
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ fontWeight: "700", fontSize: "0.92rem", color: "var(--text-main)" }}>
                            📍 {addr.label || `Shipping Address #${idx + 1}`}
                          </span>
                          {idx === 0 && (
                            <Badge variant="info" style={{ fontSize: "0.65rem" }}>
                              1st / Default
                            </Badge>
                          )}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "3px" }}>
                          {addr.addressLine1} {addr.addressLine2 ? `, ${addr.addressLine2}` : ""}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                          {addr.city}, {addr.state} {addr.pincode ? `- ${addr.pincode}` : ""}
                        </div>
                      </div>
                      <div style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: isSelected ? "5px solid var(--primary)" : "2px solid var(--border-subtle)",
                        background: isSelected ? "#fff" : "transparent",
                        flexShrink: 0,
                        marginLeft: "8px"
                      }} />
                    </div>
                  );
                })}

                {/* Option to use Billing Address */}
                <div
                  onClick={() => setSelectedShippingAddress(null)}
                  style={{
                    padding: "0.85rem",
                    background: selectedShippingAddress === null ? "rgba(59, 130, 246, 0.15)" : "var(--bg-card)",
                    border: selectedShippingAddress === null ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    transition: "all 0.15s"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>🏢 Same as Billing Address</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "3px" }}>
                      {selectedCustomer?.billingAddress?.addressLine1 || selectedCustomer?.city || "Registered office address"}
                    </div>
                  </div>
                  <div style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: selectedShippingAddress === null ? "5px solid var(--primary)" : "2px solid var(--border-subtle)",
                    background: selectedShippingAddress === null ? "#fff" : "transparent",
                    flexShrink: 0,
                    marginLeft: "8px"
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 4: LINE ITEMS WITH CUSTOMER PRICE OVERRIDES */}
        {step === 4 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: "600" }}>
                Invoice Items ({lineItems.length})
              </span>
              <span style={{ fontSize: "0.9rem", fontWeight: "700", color: "var(--accent-blue)" }}>
                Subtotal: ₹{totals.subtotal.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Line items currently in cart */}
            {lineItems.length > 0 && (
              <div style={{ marginBottom: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {lineItems.map((li, idx) => {
                  const catalogItem = items.find((i) => i.id === li.itemId);
                  const isOverridden = Number(li.rate) !== Number(catalogItem?.basicPrice || catalogItem?.sellingPrice || 0);

                  return (
                    <Card key={idx} style={{ padding: "0.75rem", marginBottom: "0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                            {li.itemName}
                            {(cartCountByItem[li.itemId] || 0) > 1 && (
                            <span style={{ marginLeft: "6px", fontSize: "0.7rem", fontWeight: "700", color: "var(--primary)" }}>
                              #{lineItems.slice(0, idx + 1).filter((x) => x.itemId === li.itemId).length} of {cartCountByItem[li.itemId]}
                            </span>
                          )}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
                            {invoiceType === INVOICE_TYPES.GST ? `${li.gstPercent}% GST` : "Non-GST Bill (0% Tax)"}
                            {isOverridden && (
                              <span style={{ marginLeft: "6px", color: "var(--primary)", fontWeight: "700" }}>
                                ⭐ Custom price for {selectedCustomer?.name}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveLine(idx)}
                          style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "2px" }}
                          aria-label="Remove item"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Interactive Rate & Quantity Controls */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "0.6rem",
                        paddingTop: "0.5rem",
                        borderTop: "1px solid var(--border-subtle)",
                        flexWrap: "wrap",
                        gap: "0.5rem"
                      }}>
                        {/* Quantity Counter */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ width: "30px", height: "30px", padding: "0", minHeight: "unset", fontSize: "1.1rem", fontWeight: "700" }}
                            onClick={() => handleQtyChange(idx, Math.max(1, (parseFloat(li.qty) || 1) - 1))}
                            title="Decrease quantity"
                          >
                            -
                          </button>
                          <NumberField
                            className=""
                            aria-label="Quantity"
                            min={0.001}
                            fallback={1}
                            value={li.qty}
                            onValueChange={(v) => handleQtyChange(idx, v)}
                            style={{
                              width: "75px",
                              textAlign: "center",
                              background: "var(--bg-input)",
                              border: "1.5px solid var(--accent-blue, #38bdf8)",
                              borderRadius: "var(--radius-sm)",
                              color: "var(--text-main)",
                              padding: "4px 2px",
                              fontWeight: "800",
                              fontSize: "0.95rem"
                            }}
                            title="Type any quantity directly (e.g. 50, 100, 2.5 kg)"
                          />
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ width: "30px", height: "30px", padding: "0", minHeight: "unset", fontSize: "1.1rem", fontWeight: "700" }}
                            onClick={() => handleQtyChange(idx, (parseFloat(li.qty) || 0) + 1)}
                            title="Increase quantity"
                          >
                            +
                          </button>
                          <span style={{ fontSize: "0.78rem", color: "var(--text-main)", fontWeight: "600" }}>{li.unit || "pcs"}</span>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ width: "30px", height: "30px", padding: "0", minHeight: "unset", fontSize: "0.95rem" }}
                            onClick={() => setQtyCalcIndex(idx)}
                            title="Add up quantities"
                            aria-label="Add up quantities"
                          >
                            🧮
                          </button>
                        </div>

                        {Array.isArray(li.qtyBreakdown) && li.qtyBreakdown.length > 1 && (
                          <div style={{ marginTop: "2px" }}>
                            <button
                              type="button"
                              onClick={() => setQtyCalcIndex(idx)}
                              style={{
                                background: "var(--primary-subtle, rgba(56, 189, 248, 0.12))",
                                border: "1px dashed var(--primary)",
                                borderRadius: "var(--radius-sm)",
                                color: "var(--primary)",
                                fontSize: "0.72rem",
                                fontWeight: "700",
                                cursor: "pointer",
                                padding: "2px 6px",
                                textAlign: "left"
                              }}
                              title="Click to view/edit partial quantity breakdown"
                            >
                              🧮 {li.qtyBreakdown.length} parts ({li.qtyBreakdown.join(" + ")})
                            </button>
                          </div>
                        )}

                        {/* Rate Input */}
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>Rate: ₹</span>
                          <NumberField
                            className=""
                            aria-label="Rate"
                            min={0}
                            fallback={0}
                            decimals={2}
                            value={li.rate}
                            onValueChange={(v) => handleRateChange(idx, v)}
                            style={{
                              width: "75px",
                              textAlign: "right",
                              background: "var(--bg-input)",
                              border: isOverridden ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)",
                              borderRadius: "var(--radius-sm)",
                              color: "var(--text-main)",
                              padding: "2px 4px",
                              fontWeight: "700",
                              fontSize: "0.85rem"
                            }}
                          />
                        </div>

                        {/* Line Total */}
                        <div style={{ fontWeight: "700", fontSize: "0.95rem", color: "var(--text-main)" }}>
                          ₹{Number(li.lineTotal).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Quick Item Picker Grid */}
            <div style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                <span style={{ fontSize: "0.82rem", fontWeight: "600", color: "var(--text-main)" }}>
                  Add Items from Inventory
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                  onClick={() => setShowAddItemSheet(true)}
                >
                  + New Item
                </button>
              </div>
              <input
                type="text"
                placeholder="Search items by name or SKU..."
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                className="form-control"
                style={{ marginBottom: "0.5rem" }}
              />

              <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {filteredCatalogItems.map((item) => {
                  const partyRate = getCustomerPriceForItem(selectedCustomer, item);
                  const hasCustomRate = selectedCustomer?.customPrices?.[item.id] !== undefined;

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleAddItem(item)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.6rem 0.75rem",
                        background: "var(--bg-card)",
                        borderRadius: "var(--radius-sm)",
                        cursor: "pointer",
                        border: "1px solid var(--border-subtle)"
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "0.85rem", fontWeight: "600" }}>{item.itemName || item.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "flex", gap: "6px" }}>
                          <span>Stock: {item.stock || 0}</span>
                          <span>•</span>
                          <span style={{ color: hasCustomRate ? "var(--primary)" : "inherit", fontWeight: hasCustomRate ? "700" : "normal" }}>
                            {hasCustomRate ? `🏷️ Party Price: ₹${partyRate}` : `₹${partyRate}`}
                          </span>
                          {cartCountByItem[item.id] > 0 && (
                            <span style={{ color: "var(--primary)", fontWeight: "700" }}>• In cart ×{cartCountByItem[item.id]}</span>
                          )}
                        </div>
                      </div>
                      <span style={{ color: "var(--primary)", fontWeight: "700", fontSize: "1.1rem" }}>+</span>
                    </div>
                  );
                })}
                {filteredCatalogItems.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", textAlign: "center", padding: "0.75rem" }}>
                    {itemSearchTerm.trim() ? `No item matches "${itemSearchTerm.trim()}".` : "No items in inventory yet."}{" "}
                    <button
                      type="button"
                      onClick={() => setShowAddItemSheet(true)}
                      style={{ background: "none", border: "none", color: "var(--primary)", fontWeight: "700", cursor: "pointer", padding: 0 }}
                    >
                      + Create new item
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: REVIEW & SAVE */}
        {step === 5 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--text-main)" }}>
                {invoiceType === INVOICE_TYPES.GST ? "GST Tax Invoice Summary" : "Non-GST / Cash Bill Summary"}
              </div>
              <Badge variant={invoiceType === INVOICE_TYPES.GST ? "info" : "warning"}>
                {invoiceType === INVOICE_TYPES.GST ? "GST Bill" : "Non-GST"}
              </Badge>
            </div>

            {/* Bill Summary Card */}
            <div style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "1rem",
              marginBottom: "1rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Billed To:</span>
                <span style={{ fontWeight: "600" }}>{selectedCustomer?.name}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Shipping Location:</span>
                <span style={{ fontWeight: "600" }}>
                  {selectedShippingAddress ? selectedShippingAddress.label : "Billing Destination"} ({selectedShippingAddress ? selectedShippingAddress.city : selectedCustomer?.city})
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Invoice / Bill #:</span>
                <span style={{ fontWeight: "600" }}>{invoiceNo}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Invoice Date:</span>
                <span style={{ fontWeight: "600" }}>{formatAppDate(invoiceDate, selectedCompany?.dateFormat)}</span>
              </div>
              {lorryNo && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                  <span style={{ color: "var(--text-muted)" }}>Lorry / Vehicle #:</span>
                  <span style={{ fontWeight: "600" }}>{lorryNo}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Total Items:</span>
                <span style={{ fontWeight: "600" }}>{lineItems.length} items</span>
              </div>
            </div>

            {/* Financial Breakdown Table */}
            <div style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "1rem",
              marginBottom: "1rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                <span style={{ color: "var(--text-muted)" }}>Items Subtotal:</span>
                <span style={{ fontWeight: "600" }}>₹{totals.subtotal.toLocaleString("en-IN")}</span>
              </div>

              {/* Discount Input */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0.5rem 0" }}>
                <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Discount (₹):</span>
                <NumberField
                  className=""
                  aria-label="Discount"
                  min={0}
                  fallback={0}
                  decimals={2}
                  value={Number(discountInput) || 0}
                  onValueChange={(v) => setDiscountInput(v)}
                  style={{
                    width: "80px",
                    textAlign: "right",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-sm)",
                    color: "var(--text-main)",
                    padding: "3px 6px",
                    fontSize: "0.85rem"
                  }}
                />
              </div>

              {invoiceType === INVOICE_TYPES.GST ? (
                totals.isInterState ? (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>IGST (Integrated Tax):</span>
                    <span style={{ fontWeight: "600" }}>₹{totals.igst.toLocaleString("en-IN")}</span>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                      <span style={{ color: "var(--text-muted)" }}>CGST (Central Tax):</span>
                      <span style={{ fontWeight: "600" }}>₹{totals.cgst.toLocaleString("en-IN")}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                      <span style={{ color: "var(--text-muted)" }}>SGST (State Tax):</span>
                      <span style={{ fontWeight: "600" }}>₹{totals.sgst.toLocaleString("en-IN")}</span>
                    </div>
                  </>
                )
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", color: "var(--text-dim)" }}>
                  <span>Tax Status:</span>
                  <span>Non-GST Bill (0% Tax)</span>
                </div>
              )}

              {totals.roundOff !== 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", color: "var(--text-dim)" }}>
                  <span>Round Off:</span>
                  <span>₹{totals.roundOff}</span>
                </div>
              )}

              <div style={{ borderTop: "1px solid var(--border-subtle)", margin: "0.6rem 0" }} />

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1rem", fontWeight: "800" }}>
                <span>Grand Total:</span>
                <span style={{ color: "var(--accent-blue)" }}>
                  ₹{totals.grandTotal.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* Inform user of custom rate memorization */}
            {Object.keys(customPriceOverrides).length > 0 && (
              <div style={{
                background: "rgba(59, 130, 246, 0.08)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
                padding: "0.65rem 0.85rem",
                borderRadius: "var(--radius-md)",
                marginBottom: "1rem",
                fontSize: "0.78rem",
                color: "#93c5fd"
              }}>
                💾 <strong>Customer Price Memory</strong>: Updated rates for {Object.keys(customPriceOverrides).length} item(s) will be remembered for <strong>{selectedCustomer?.name}</strong> on future invoices. If an item is on several lines, the last rate you edited is remembered.
              </div>
            )}

            {/* Edit: what saving changes */}
            {editChange && (
              <div data-testid="edit-changes" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.75rem 0.85rem", marginBottom: "1rem", fontSize: "0.82rem" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "0.4rem" }}>What changes</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                  <span style={{ color: "var(--text-muted)" }}>Total</span>
                  <span style={{ fontWeight: 700 }}>₹{editChange.oldTotal.toLocaleString("en-IN")} → ₹{editChange.newTotal.toLocaleString("en-IN")}</span>
                </div>
                {editChange.newPaid > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Received</span>
                    <span>₹{editChange.newPaid.toLocaleString("en-IN")}{editChange.receipts.length ? ` (${editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ")})` : " (paid at billing)"} • Balance ₹{editChange.newBalance.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {editChange.stock.map((st) => (
                  <div key={st.itemId} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Stock • {st.itemName}</span>
                    <span>{st.stockChange > 0 ? `+${st.stockChange} back to stock` : `−${Math.abs(st.stockChange)} from stock`}</span>
                  </div>
                ))}
                {editChange.dues.map((d) => (
                  <div key={d.customerId} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Due • {d.name}</span>
                    <span>{d.delta > 0 ? "+" : "−"}₹{Math.abs(d.delta).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                {editChange.overReceived > 0 && (
                  <div className="form-error" style={{ marginTop: "0.6rem" }}>
                    ⚠ Received ₹{editChange.newPaid.toLocaleString("en-IN")} is more than the new total ₹{editChange.newTotal.toLocaleString("en-IN")}.
                    Edit or void {editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ") || "the receipt"} first (in this invoice's payment history).
                    <div style={{ marginTop: "0.4rem" }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close & fix receipt</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Mode Selection (when editing, only for bills without recorded receipts) */}
            {!(editChange?.receipts.length) && (
            <div className="form-group">
              <label className="form-label">Payment Received</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <button
                  type="button"
                  className={`btn ${paymentOption === "Unpaid" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setPaymentOption("Unpaid")}
                  style={{ minHeight: "40px", fontSize: "0.85rem" }}
                >
                  ⏳ Unpaid (Add to Ledger)
                </button>
                <button
                  type="button"
                  className={`btn ${paymentOption === "Paid" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setPaymentOption("Paid")}
                  style={{ minHeight: "40px", fontSize: "0.85rem" }}
                >
                  💵 Paid in Full
                </button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
          {step > 1 ? (
            <Button
              type="button"
              variant="secondary"
              block
              onClick={handleBack}
            >
              ← Back
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              block
              onClick={onClose}
            >
              Cancel
            </Button>
          )}

          {step < 5 ? (
            <Button
              type="button"
              variant="primary"
              block
              onClick={handleNext}
            >
              {step === 2 && selectedShippingAddress
                ? "Next: Shipping / Items →"
                : "Next Step →"}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              block
              loading={submitting}
              disabled={editChange?.overReceived > 0}
              onClick={handleFinalSubmit}
            >
              {isEditing ? "Save changes" : `Confirm & Create ${invoiceType === INVOICE_TYPES.NORMAL ? "Bill" : "Invoice"}`}
            </Button>
          )}
        </div>
      </div>
    </BottomSheet>

    <BottomSheet
      isOpen={showAddItemSheet}
      onClose={() => setShowAddItemSheet(false)}
      title="New Item / Product"
    >
      <ItemForm
        initialData={newItemPrefill}
        onSubmit={handleSaveItemInline}
        onCancel={() => setShowAddItemSheet(false)}
        loading={savingNewItem}
      />
    </BottomSheet>

    <QuantityCalculatorSheet
      isOpen={qtyCalcIndex !== null}
      onClose={() => setQtyCalcIndex(null)}
      itemName={lineItems[qtyCalcIndex]?.itemName}
      unit={lineItems[qtyCalcIndex]?.unit || "pcs"}
      initialRows={
        lineItems[qtyCalcIndex]?.qtyBreakdown ||
        (lineItems[qtyCalcIndex]?.qty > 0 ? [lineItems[qtyCalcIndex]?.qty] : null)
      }
      onApply={(total, breakdown) => handleQtyCalcApply(qtyCalcIndex, total, breakdown)}
    />
    </>
  );
}

export default InvoiceCreateFlow;
